import { useCallback, useEffect } from 'react';
import { isAfter } from 'date-fns';
import { useSelector } from 'react-redux';

import { NotificationId } from '@/modules/api/codegen';
import { useUser } from '@/modules/auth/hooks';
import { StaticModal, useModal } from '@/modules/modals';
import { useNotification } from '@/modules/notifications/hooks';
import { SubscriptionType } from '@/modules/settings/constants';
import { selectGeneralSettings } from '@/modules/settings/services/selectors';

export type TrialModalType = 'start' | 'end';

export const useTrialModal = () => {
    const { modal, updateModal } = useModal<{ type: TrialModalType }>(StaticModal.TRIAL);

    const {
        notification: trialEndNotification,
        update: trialEndNotificationUpdate,
        isLoading: isTrialEndNotificationLoading,
    } = useNotification(NotificationId.largeWorkspaceTrialEnd);

    const user = useUser();
    const settings = useSelector(selectGeneralSettings);

    const openTrialModal = useCallback(
        (type: TrialModalType) => {
            updateModal(true, { type });
        },
        [updateModal],
    );

    const closeTrialModal = useCallback(
        async (type: TrialModalType) => {
            updateModal(false);

            if (type === 'end') {
                await trialEndNotificationUpdate.rejectNotification();
            }
        },
        [updateModal, trialEndNotificationUpdate],
    );

    useEffect(() => {
        if (!user?.isAdmin || !settings) {
            return;
        }

        if (user.isFirstLoginSaas && settings.subscription === SubscriptionType.trial) {
            openTrialModal('start');

            return;
        }

        if (
            settings.trialEndTimestamp &&
            isAfter(new Date(), new Date(settings.trialEndTimestamp)) &&
            !isTrialEndNotificationLoading &&
            !trialEndNotification?.dismissedAt
        ) {
            openTrialModal('end');

            return;
        }
    }, [user?.isAdmin, user?.isFirstLoginSaas, settings, openTrialModal, trialEndNotification, isTrialEndNotificationLoading]);

    return {
        modal,
        closeTrialModal,
    };
};
